'use client';

import { useEffect, useRef, useState } from 'react';

import { showSuccessToast, showWarningToast } from '@/components/ui/sonner';
import { useProfileStore, type UserData } from '@/store/profile.store';
import {
  changeEmailReq,
  confirmEmailVerificationReq,
  EmailVerificationRequestError,
  sendEmailVerificationCodeReq,
} from '../api';
import {
  CONFIRM_EMAIL_BUTTON_ID,
  EMAIL_VERIFICATION_COPY,
  PROFILE_EMAIL_SECTION_ID,
  VERIFICATION_CODE_INPUT_ID,
} from '../consts';
import type { ProfileEmailMode } from '../types';
import {
  getResendSecondsLeft,
  isValidEmailAddress,
  mergeEmailVerificationUser,
  normalizeEmailAddress,
  resolveProfileEmailPill,
  resolveProfileEmailView,
} from '../utils';

export function useProfileEmailSection() {
  const userData = useProfileStore((state) => state.userData);
  const setUserData = useProfileStore((state) => state.setUserData);
  const [mode, setMode] = useState<ProfileEmailMode>('idle');
  const [codeRequested, setCodeRequested] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [codeError, setCodeError] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [passwordDraft, setPasswordDraft] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const didFocusSectionRef = useRef(false);
  const resendAvailableAt = userData?.resendAvailableAt;

  const view = resolveProfileEmailView({
    status: userData?.verificationStatus,
    mode,
    email: userData?.email ?? '',
    pendingEmail: userData?.pendingEmail,
    codeSentAt: userData?.codeSentAt,
    codeRequested,
  });
  const pill = resolveProfileEmailPill(view);
  const secondsLeft = getResendSecondsLeft(userData?.resendAvailableAt, nowMs);

  useEffect(() => {
    if (getResendSecondsLeft(resendAvailableAt, Date.now()) <= 0) return;

    const timer = window.setInterval(() => {
      const nextNow = Date.now();
      setNowMs(nextNow);
      if (getResendSecondsLeft(resendAvailableAt, nextNow) <= 0) {
        window.clearInterval(timer);
      }
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendAvailableAt]);

  useEffect(() => {
    if (window.location.hash !== `#${PROFILE_EMAIL_SECTION_ID}` || !userData) return;

    const frame = window.requestAnimationFrame(() => {
      if (didFocusSectionRef.current) return;
      didFocusSectionRef.current = true;
      document.getElementById(PROFILE_EMAIL_SECTION_ID)?.scrollIntoView({ block: 'center' });
      const focusTargetId =
        view.kind === 'code' ? VERIFICATION_CODE_INPUT_ID : CONFIRM_EMAIL_BUTTON_ID;
      document.getElementById(focusTargetId)?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [userData, view.kind]);

  const applyUser = (nextUser: Partial<UserData> & { email?: string | null }) => {
    const confirmed = nextUser.verificationStatus === 'email' && !nextUser.pendingEmail;
    setUserData(
      mergeEmailVerificationUser(useProfileStore.getState().userData, {
        ...nextUser,
        ...(confirmed ? { pendingEmail: null, codeSentAt: null, resendAvailableAt: null } : {}),
      }),
    );
  };

  const showRequestError = (
    error: unknown,
    fallback: string,
    target: 'email' | 'password' | 'code' | 'toast',
  ) => {
    if (error instanceof EmailVerificationRequestError && target !== 'toast') {
      if (target === 'email') {
        setEmailError(error.message);
        return;
      }
      if (target === 'password') {
        setPasswordError(error.message);
        return;
      }
      setCodeError(error.message);
      return;
    }
    showWarningToast(error instanceof Error ? error.message : fallback);
  };

  const handleSendCode = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setCodeError('');
    try {
      const nextUser = await sendEmailVerificationCodeReq();
      applyUser(nextUser);
      setCodeRequested(true);
      setCodeValue('');
      setMode('idle');
    } catch (error) {
      showRequestError(error, EMAIL_VERIFICATION_COPY.sendFailed, 'toast');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (isSubmitting || secondsLeft > 0) return;
    setIsSubmitting(true);
    setCodeError('');
    try {
      const nextUser =
        userData?.pendingEmail && passwordDraft
          ? await changeEmailReq(userData.pendingEmail, passwordDraft)
          : await sendEmailVerificationCodeReq();
      applyUser(nextUser);
      setCodeRequested(true);
      setCodeValue('');
      showSuccessToast(EMAIL_VERIFICATION_COPY.codeResentToast);
    } catch (error) {
      showRequestError(error, EMAIL_VERIFICATION_COPY.sendFailed, 'toast');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmCode = async () => {
    const trimmedCode = codeValue.trim();
    if (!trimmedCode) {
      setCodeError(EMAIL_VERIFICATION_COPY.emptyCode);
      return;
    }
    if (isSubmitting) return;

    setIsSubmitting(true);
    setCodeError('');
    try {
      const nextUser = await confirmEmailVerificationReq(trimmedCode);
      setCodeRequested(false);
      setMode('idle');
      setCodeValue('');
      applyUser(nextUser);
      setPasswordDraft('');
      setPasswordError('');
      showSuccessToast(EMAIL_VERIFICATION_COPY.confirmedToast);
    } catch (error) {
      showRequestError(error, EMAIL_VERIFICATION_COPY.confirmFailed, 'code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartChange = () => {
    setEmailDraft('');
    setPasswordDraft('');
    setEmailError('');
    setPasswordError('');
    setMode('change');
  };

  const handleCancelChange = () => {
    setEmailDraft('');
    setPasswordDraft('');
    setEmailError('');
    setPasswordError('');
    setMode('idle');
  };

  const handleEmailDraftChange = (value: string) => {
    setEmailDraft(value);
    if (emailError) setEmailError('');
    if (passwordError === EMAIL_VERIFICATION_COPY.domainForbiddenChange) {
      setPasswordError('');
    }
  };

  const handlePasswordDraftChange = (value: string) => {
    setPasswordDraft(value);
    if (passwordError) setPasswordError('');
  };

  const handleCodeChange = (value: string) => {
    setCodeValue(value);
    if (codeError) setCodeError('');
  };

  const handleSubmitChange = async () => {
    const normalizedEmail = normalizeEmailAddress(emailDraft);
    const trimmedPassword = passwordDraft.trim();
    let hasValidationError = false;

    if (!isValidEmailAddress(normalizedEmail)) {
      setEmailError(EMAIL_VERIFICATION_COPY.invalidEmail);
      hasValidationError = true;
    } else {
      setEmailError('');
    }

    if (!trimmedPassword) {
      setPasswordError(EMAIL_VERIFICATION_COPY.emptyPassword);
      hasValidationError = true;
    } else {
      setPasswordError('');
    }

    if (hasValidationError || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const nextUser = await changeEmailReq(normalizedEmail, trimmedPassword);
      applyUser(nextUser);
      setCodeRequested(true);
      setCodeValue('');
      setCodeError('');
      setMode('idle');
      showSuccessToast(EMAIL_VERIFICATION_COPY.changeCodeSentToast);
    } catch (error) {
      showRequestError(error, EMAIL_VERIFICATION_COPY.changeFailed, 'password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    view,
    pill,
    emailDraft,
    passwordDraft,
    emailError,
    passwordError,
    codeValue,
    codeError,
    secondsLeft,
    isSubmitting,
    handleSendCode,
    handleResendCode,
    handleConfirmCode,
    handleStartChange,
    handleCancelChange,
    handleEmailDraftChange,
    handlePasswordDraftChange,
    handleCodeChange,
    handleSubmitChange,
  };
}
